import React, { MouseEvent, useCallback } from 'react';
import { Promotion } from 'types/utils';
import { analyticsUtils } from 'utils/analytics-utils';

export type WithGAPromoClickProps<T> = {
  bannerRefs?: React.MutableRefObject<T[]>;
  elementBlock: string;
  promotion: Promotion;
  position: number;
  onClick?: (e: MouseEvent<T>) => void;
  href?: string;
};

/**
 * HOC, который добавляет компоненту обработчик события вида "GA:promoClick" при срабатывании события "onClick"
 */
export function withGAPromoClick<T, P extends WithGAPromoClickProps<T>>(WrappedComponent: React.FC<P>) {
  return ({
    bannerRefs,
    elementBlock,
    position,
    promotion,
    onClick,
    ...props
  }: P & WithGAPromoClickProps<T>) => {
    /**
     * Обработчик события onClick с отправкой "GA:promoView"
     */
    const handleBannerClick = useCallback(
      (e: MouseEvent<T>) => {
        if (onClick) {
          onClick(e);
        }

        analyticsUtils.promoClickEvent(elementBlock, promotion);
      },
      [elementBlock, promotion, onClick]
    );

    /**
     * Обработчик события onAuxClick с отправкой "GA:promoView"
     */
    const handleWheelClick = useCallback(
      (e: MouseEvent) => {
        // Проверка, что кликнута именно средняя кнопка/колёсико
        if (e.button === 1) {
          analyticsUtils.promoClickEvent(elementBlock, promotion);
        }
      },
      [elementBlock, promotion]
    );

    return props.href ? (
      <WrappedComponent
        data-position={position}
        onAuxClick={handleWheelClick}
        onClick={handleBannerClick}
        refCb={(el: T | null) => {
          if (el && bannerRefs) bannerRefs.current[position] = el;
        }}
        {...(props as P)}
      />
    ) : (
      <WrappedComponent
        data-position={position}
        onClick={handleBannerClick}
        refCb={(el: T | null) => {
          if (el && bannerRefs) bannerRefs.current[position] = el;
        }}
        {...(props as P)}
      />
    );
  };
}
